import {useState} from "react";

import classNames from "classnames";
import styles from "./styles/SocialItemModal.module.scss";
import {observer} from 'mobx-react';

import FontAwesome from "../utilities/FontAwesome";
import ImageComponent from "../utilities/ImageComponent";
import Modal from "./Modal";
import Moment from "moment";
import {getPlatform, getSocialIcon} from "../../utils/StringUtilities";
import RoundIcon from "../admin/utilities/RoundIcon";
import FlatIcon from "../utilities/FlatIcon";
import SocialMarkdown from "../utilities/SocialMarkdown";
import {sourceTypesEnum} from "../../utils/SourceUtils";
import {isMedia, isNews} from "../schoolfeed/ListItemGroup";
import {ContentItemVideoComponent} from "../blocks/TerracedGridTheme/GalleryBlock/VideoGalleryItem";
import {IReactiveMediaContentItem, ReactiveContentItem,} from "../../stores/SchoolFeedStore";
import {MeritChatThread} from "../utilities/MeritChat";
import ClickableLink from "../utilities/ClickableLink";
import {EditButton} from "../blocks/TerracedGridTheme/EditButton";
import {useStores} from "../../stores/useStores";
import {UnpublishedContentItemInfoBox} from "./NewsModal/NewsModalContent";

const imagePreviewClassName = classNames({
    [styles.imagePreview]: true,
});
const thumbnailClassName = classNames({
    [styles.thumbnail]: true,
});
const titleSectionClassName = classNames({
    [styles.titleSection]: true,
});
const thirdPartyBoxClassName = classNames({
    [styles.thirdPartyBox]: true,
});
const timeClassName = classNames({
    [styles.typeNewsColBodyTime]: true,
});
const contentContainerClassName = classNames({
    [styles.contentContainer]: true,
});

export type SocialItemModalNavigationProps = {
    initialItemIndex: number,
    totalItemCount: number,
    getItemAtIndex: (idx: number) => Promise<ReactiveContentItem | IAccordionItem | null> | ReactiveContentItem | IAccordionItem | null,
};

export function ReadMoreButton(props: { itemObj: ReactiveContentItem }) {

    const attribution = getPlatform(props.itemObj.type);

    const viewBtnClassName = classNames({
        [styles.viewBtn]: true,
        [styles.viewBtnFacebook]: attribution === sourceTypesEnum.FACEBOOK,
        [styles.viewBtnTwitter]: attribution === sourceTypesEnum.TWITTER,
    });
    const typeNewsColBodyAttributionClassName = classNames({
        [styles.typeNewsColBodyAttribution]: true,
        [styles.fontColorFacebook]: attribution === sourceTypesEnum.FACEBOOK,
        [styles.fontColorTwitter]: attribution === sourceTypesEnum.TWITTER,
    });

    const attributionTag = (
        <FontAwesome
            key="attribution"
            fixedWidth={true}
            ariaHidden={true}
            name={getSocialIcon(attribution, true)}
            className={typeNewsColBodyAttributionClassName}
        />
    );

    return <ClickableLink href={props.itemObj.link} className={viewBtnClassName} role="button">
        {attributionTag}
        {props.itemObj.linkText}
    </ClickableLink>
}

function SocialItemModalImage(props: { itemObj: ReactiveContentItem }) {
    const imageUrl = props.itemObj.image;
    if (isNews(props.itemObj) && imageUrl) {
        return <ImageComponent
            src={imageUrl}
            key={props.itemObj.id}
            alt={props.itemObj.title || "Social Post Image"}
            className={imagePreviewClassName}/>;
    } else if (isMedia(props.itemObj)) {
        if (props.itemObj.type.includes('photo')) {
            return (props.itemObj.json_data.images as MediaImage[]).length > 0 ? <ImageComponent
                src={imageUrl}
                key={props.itemObj.id}
                alt={props.itemObj.title ? props.itemObj.title : 'Untitled Image'}
            /> : <></>;
        } else if (props.itemObj.type.includes("video") && (props.itemObj.json_data.videos as { videos_list: MediaVideo[] }).videos_list?.length > 0) {
            return <ContentItemVideoComponent item={props.itemObj}/>
        }
    }
    return <></>;
}

const SocialItemModal = observer((props: {
    index: number,
    modalObj: {
        itemObj: ReactiveContentItem,
        handleDelete: (c: ContentItem) => void,
    }
    active: boolean
}) => {
    const {userStore} = useStores();
    const attribution = getPlatform(props.modalObj.itemObj.type);
    const [validImage, setValidImage] = useState(true);

    const modalBodyClassName = classNames({
        [styles.modalBody]: true,

        [styles.modalBodyFacebook]: attribution === sourceTypesEnum.FACEBOOK,
        [styles.modalBodyTwitter]: attribution === sourceTypesEnum.TWITTER,
    });

    let displayDate = props.modalObj.itemObj.created_at ?
        <span key="timestamp" className={timeClassName}>{Moment(props.modalObj.itemObj.created_at).fromNow()}</span> : "";

    let sourceTitle;
    if (props.modalObj.itemObj.source?.account_title) {
        sourceTitle = <h1>{props.modalObj.itemObj.source.account_title}</h1>;
    } else if (props.modalObj.itemObj.organization) {
        sourceTitle = <h1 className={"notranslate"}>{(props.modalObj.itemObj.organization as BaseOrganization).title}</h1>
    }
    let title = props.modalObj.itemObj.title ? <h2 key="title" style={{fontWeight: 'bold'}}>{props.modalObj.itemObj.title}</h2> : "";
    let description = props.modalObj.itemObj.description ?
        <SocialMarkdown platform={attribution} text={props.modalObj.itemObj.description}/> : "";

    let accountImage;
    if (props.modalObj.itemObj?.source?.account_image && validImage) {
        accountImage = <div className={thumbnailClassName}>
            <ImageComponent src={props.modalObj.itemObj.source?.account_image}
                            alt={props.modalObj.itemObj.source?.account_title || 'Untitled'}
                            onError={() => setValidImage(false)}/>
        </div>
    } else if (attribution !== sourceTypesEnum.CUSTOM) {
        accountImage = <RoundIcon platform={attribution}><FlatIcon name={getSocialIcon(attribution)}/></RoundIcon>
    }

    return (
        <Modal type={'news'} size={'lg'} index={props.index} active={props.active}>
            <article className={modalBodyClassName}>
                {!props.modalObj.itemObj.published && <UnpublishedContentItemInfoBox unpublishedItemType={"Gallery Item"} item={props.modalObj.itemObj}/>}
                <header>
                    {accountImage}
                    <div style={{paddingLeft: "0.5em"}} className={titleSectionClassName}>
                        {sourceTitle}
                        {displayDate}
                    </div>
                </header>
                <section className={contentContainerClassName}>
                    {title && <div className={thirdPartyBoxClassName}>
                        {title}
                    </div>}
                    {userStore.isEditor && <EditButton contentItem={props.modalObj.itemObj as IReactiveMediaContentItem} handleDelete={props.modalObj.handleDelete}/>}
                    <SocialItemModalImage itemObj={props.modalObj.itemObj} />
                    {description}
                </section>
                {props.modalObj.itemObj.third_party_url && <footer>
                    <ReadMoreButton itemObj={props.modalObj.itemObj} />
                </footer>}
                {process.env.MERITOCRACY_CHAT_ENABLED && props.modalObj.itemObj.meritchat_enabled &&
                    <MeritChatThread
                        source_id={props.modalObj.itemObj.id}
                        source_category={props.modalObj.itemObj.type}
                        organization_id={props.modalObj.itemObj.organization_id}
                    />}
            </article>
        </Modal>
    );
});

export default SocialItemModal;
